/**
 * Contraste WCAG : de quoi garantir qu'un texte reste lisible sur un fond que
 * l'utilisateur a choisi lui-même.
 *
 * Module pur, sans React ni dépendance.
 */

/** Seuil WCAG AA pour du texte de taille courante. */
export const MIN_CONTRAST_RATIO = 4.5;

/** Seuil WCAG AA pour du grand texte : celui retenu pour les titres. */
export const MIN_LARGE_TEXT_CONTRAST_RATIO = 3;

const HEX_SHORT = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_LONG = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX_ALPHA = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RGB_FUNC = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*(?:[,/]\s*([\d.%]+)\s*)?\)$/i;

/**
 * Une couleur CSS → ses composantes et son opacité `a` (de 0 à 1), ou `null`
 * si on ne sait pas la lire (dégradé, variable CSS…).
 */
function parseRgba(value) {
  if (typeof value !== 'string') return null;
  const input = value.trim();

  if (input.toLowerCase() === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };

  const short = input.match(HEX_SHORT);
  if (short) {
    return {
      r: parseInt(short[1] + short[1], 16),
      g: parseInt(short[2] + short[2], 16),
      b: parseInt(short[3] + short[3], 16),
      a: 1,
    };
  }

  const alpha = input.match(HEX_ALPHA);
  if (alpha) {
    return {
      r: parseInt(alpha[1], 16),
      g: parseInt(alpha[2], 16),
      b: parseInt(alpha[3], 16),
      a: parseInt(alpha[4], 16) / 255,
    };
  }

  const long = input.match(HEX_LONG);
  if (long) {
    return {
      r: parseInt(long[1], 16),
      g: parseInt(long[2], 16),
      b: parseInt(long[3], 16),
      a: 1,
    };
  }

  const func = input.match(RGB_FUNC);
  if (func) {
    let a = 1;
    if (func[4] !== undefined) {
      a = func[4].endsWith('%') ? Number(func[4].slice(0, -1)) / 100 : Number(func[4]);
      if (Number.isNaN(a)) return null;
    }
    const channels = [func[1], func[2], func[3]].map((c) => Math.round(Number(c)));
    if (channels.some((c) => Number.isNaN(c) || c < 0 || c > 255)) return null;
    return { r: channels[0], g: channels[1], b: channels[2], a: Math.min(1, Math.max(0, a)) };
  }

  return null;
}

/**
 * Une couleur CSS → ses composantes, ou `null` si on ne peut pas savoir de
 * quoi il s'agit.
 *
 * `null` couvre aussi les couleurs **partiellement transparentes** : ce qu'on
 * verrait à l'écran dépend alors de ce qu'il y a dessous, qu'on ignore. Mieux
 * vaut refuser de conclure que conclure de travers — un dégradé, une variable
 * CSS ou `transparent` tombent dans le même cas.
 */
export function parseColor(value) {
  const color = parseRgba(value);
  if (!color || color.a < 1) return null;
  return { r: color.r, g: color.g, b: color.b };
}

/** Luminance relative WCAG 2.1. */
export function relativeLuminance({ r, g, b }) {
  const channel = (value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastBetween(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [light, dark] = la >= lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

/** Rapport de contraste entre deux couleurs, de 1 (identiques) à 21. */
export function contrastRatio(colorA, colorB) {
  const a = parseColor(colorA);
  const b = parseColor(colorB);
  if (!a || !b) return null;
  return contrastBetween(a, b);
}

/** Du noir ou du blanc — celui des deux qui se détache le mieux du fond. */
export function readableTextColor(background) {
  const white = contrastRatio('#ffffff', background);
  const black = contrastRatio('#000000', background);
  if (white === null || black === null) return null;
  return white >= black ? '#ffffff' : '#000000';
}

/**
 * La couleur voulue si elle est lisible sur ce fond, sinon du noir ou du blanc.
 *
 * Quand le fond n'est pas analysable — dégradé, variable CSS, transparence — on
 * **rend la couleur d'origine inchangée**. C'est un refus de conclure, pas un
 * échec : le texte reste alors aussi visible que le reste de son voisinage, ce
 * qui est le pire cas acceptable.
 */
export function ensureReadable(color, background, minRatio = MIN_CONTRAST_RATIO) {
  const ratio = contrastRatio(color, background);
  if (ratio === null) return color;
  if (ratio >= minRatio) return color;
  return readableTextColor(background) || color;
}

/** Une couleur, éventuellement translucide, posée sur une couleur opaque. */
const composite = (top, bottom) => ({
  r: top.r * top.a + bottom.r * (1 - top.a),
  g: top.g * top.a + bottom.g * (1 - top.a),
  b: top.b * top.a + bottom.b * (1 - top.a),
});

const NO_BACKGROUND = { r: 0, g: 0, b: 0, a: 0 };

/**
 * Contraste réellement vu : le fond (absent ou translucide) mélangé à la
 * surface qu'il laisse voir, puis le texte (translucide ou non) sur ce mélange.
 */
function visibleContrast(textColor, background, surface) {
  const base = parseColor(surface);
  const text = parseRgba(textColor);
  const layer = background == null || background === '' ? NO_BACKGROUND : parseRgba(background);
  if (!base || !text || !layer) return null;
  const backdrop = composite(layer, base);
  return contrastBetween(composite(text, backdrop), backdrop);
}

/**
 * Fond de secours pour un **aperçu** dont le texte disparaîtrait sur sa
 * surface habituelle, ou `null` quand il n'y a rien à changer.
 *
 * Contrairement à `ensureReadable`, la couleur du texte n'est jamais touchée :
 * un titre blanc sur fond transparent, pensé pour une box dessin colorée, doit
 * s'afficher blanc. C'est la surface derrière l'aperçu qu'on remplace.
 *
 * `null` aussi quand remplacer la surface n'y changerait rien : fond opaque qui
 * la recouvre, texte entièrement transparent, couleur que le module ne sait
 * pas lire.
 */
export function previewBackdrop(
  textColor,
  background,
  { surface = '#ffffff', fallback = '#262626', minRatio = MIN_CONTRAST_RATIO } = {}
) {
  const current = visibleContrast(textColor, background, surface);
  if (current === null || current >= minRatio) return null;
  const alternative = visibleContrast(textColor, background, fallback);
  return alternative !== null && alternative > current ? fallback : null;
}
